import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useNotifications,
  useDeleteNotification,
  useBulkDeleteNotifications,
  useNotificationPurgePreview,
  useRetentionPolicies,
  useSaveRetentionPolicy,
} from '../../services/httpServices/queries';
import type { RetentionUnit } from '../../types/retention';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { RetentionPurgeModal } from '../../components/ui/RetentionPurgeModal';
import { formatOrdinalDateTime } from '../../utils/dateFormat';
import { humanize } from '../../utils/textFormat';
import { toast } from 'sonner';
import {
  Bell,
  Loader2,
  AlertCircle,
  Mail,
  MessageSquare,
  Smartphone,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Search,
  X,
  Trash2,
  CalendarClock,
} from 'lucide-react';

const CHANNEL_ICONS: Record<string, any> = {
  sms: Smartphone,
  email: Mail,
  in_app: Bell,
  whatsapp: MessageSquare,
};

const CHANNEL_LABELS: Record<string, string> = {
  sms: 'SMS',
  email: 'Email',
  in_app: 'In App',
  whatsapp: 'WhatsApp',
};

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-warning/10 text-warning',
  sent: 'bg-primary/10 text-primary',
  failed: 'bg-danger/10 text-danger',
  read: 'bg-success/10 text-success',
};

export function Notifications() {
  const navigate = useNavigate();
  const location = useLocation();
  const limit = 20;

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });
  const [search, setSearch] = useState(() => params.get('search') || '');
  const debouncedSearch = useDebounce(search, 350);

  const { data, isLoading, error } = useNotifications(page, limit, debouncedSearch || undefined);
  const notifications = useMemo(() => data?.data || [], [data]);
  const meta = data?.meta;

  const deleteNotification = useDeleteNotification();
  const bulkDeleteNotifications = useBulkDeleteNotifications();
  const saveRetentionPolicy = useSaveRetentionPolicy();
  const { data: retentionData } = useRetentionPolicies();

  // Row selection for bulk actions.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  // Retention purge ("remove old notifications") state.
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [retentionValue, setRetentionValue] = useState('90');
  const [retentionUnit, setRetentionUnit] = useState<RetentionUnit>('days');
  const parsedRetention = Number(retentionValue);
  const retentionValid = Number.isInteger(parsedRetention) && parsedRetention > 0;
  const {
    data: purgePreview,
    isFetching: purgePreviewLoading,
    isError: purgePreviewError,
  } = useNotificationPurgePreview(
    retentionValid ? parsedRetention : 0,
    retentionUnit,
    purgeOpen,
  );

  const openPurge = () => {
    const policy = retentionData?.data?.policies?.find(
      (p) => p.resource === 'notifications',
    );
    setRetentionValue(String(policy?.value ?? 90));
    setRetentionUnit(policy?.unit ?? 'days');
    setPurgeOpen(true);
  };

  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', String(page));
    if (debouncedSearch) newParams.set('search', debouncedSearch);
    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [page, debouncedSearch, navigate, location.pathname]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(notifications.map((n: any) => n.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [notifications]);

  const allSelected = notifications.length > 0 && notifications.every((n: any) => selectedIds.has(n.id));
  const someSelected = notifications.some((n: any) => selectedIds.has(n.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected = notifications.length > 0 && notifications.every((n: any) => prev.has(n.id));
      return everySelected ? new Set() : new Set(notifications.map((n: any) => n.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteNotification.mutateAsync(deleteTarget.id);
      toast.success('Notification deleted');
    } catch {
      toast.error('Failed to delete notification');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleConfirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    try {
      await bulkDeleteNotifications.mutateAsync(ids);
      setSelectedIds(new Set());
      toast.success(`Deleted ${ids.length} notification${ids.length === 1 ? '' : 's'}`);
    } catch {
      toast.error('Failed to delete some notifications');
    } finally {
      setBulkConfirmOpen(false);
    }
  };

  const handleSavePolicy = async (value: number, unit: RetentionUnit) => {
    try {
      await saveRetentionPolicy.mutateAsync({
        resource: 'notifications',
        value,
        unit,
      });
      setPurgeOpen(false);
      toast.success('Automatic notification cleanup saved');
    } catch {
      toast.error('Failed to save cleanup settings');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Bell className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-text">Notifications</h1>
        </div>
        <button
          type="button"
          onClick={openPurge}
          className="px-3 py-2 text-sm font-medium text-danger border border-danger/30 hover:bg-danger/10 rounded-lg transition-colors flex items-center gap-1.5 whitespace-nowrap"
        >
          <CalendarClock size={16} />
          Remove old notifications
        </button>
      </div>

      {/* Search Bar */}
      <div className="bg-surface rounded-xl border border-border p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search by recipient, type, content, channel or status..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          {search && (
            <button
              onClick={() => { setSearch(''); setPage(1); }}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeleteNotifications.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteNotifications.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkDeleteNotifications.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load notifications</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={notifications.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Channel</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Recipient</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Message</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Sent</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {notifications.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-text-muted">
                      {search ? (
                        <span>No notifications match your search.{' '}
                          <button onClick={() => { setSearch(''); setPage(1); }} className="text-primary hover:underline">
                            Clear search
                          </button>
                        </span>
                      ) : (
                        'No notifications found'
                      )}
                    </td>
                  </tr>
                ) : (
                  notifications.map((notification: any) => {
                    const ChannelIcon = CHANNEL_ICONS[notification.channel] || Bell;
                    const status = notification.status as string;
                    const displayName = notification.userName || notification.recipient || '—';
                    const contact = notification.userName ? notification.recipient : null;
                    return (
                      <tr key={notification.id} className="hover:bg-background/50 transition-colors">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(notification.id)}
                            onChange={() => toggleOne(notification.id)}
                            className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <ChannelIcon size={14} className="text-primary" />
                            </div>
                            <span className="text-sm text-text">
                              {CHANNEL_LABELS[notification.channel] || humanize(notification.channel)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-text">{humanize(notification.notificationType)}</td>
                        <td className="px-4 py-3">
                          <div className="min-w-0">
                            <span className="block text-sm text-text truncate">{displayName}</span>
                            {contact && (
                              <span className="block text-xs text-text-muted truncate font-mono">{contact}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-2 text-sm text-text-muted max-w-xs">
                            <FileText size={14} className="shrink-0 mt-0.5" />
                            <span className="break-words line-clamp-2" title={notification.content || ''}>
                              {notification.content || '—'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[status] || 'bg-text-muted/10 text-text-muted'}`}>
                            {status === 'read' && <CheckCircle size={12} />}
                            {status === 'sent' && <CheckCircle size={12} />}
                            {status === 'failed' && <XCircle size={12} />}
                            {status === 'pending' && <Clock size={12} />}
                            {humanize(status)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-sm text-text-muted whitespace-nowrap">
                            <Clock size={14} />
                            {notification.sentAt ? formatOrdinalDateTime(notification.sentAt) : 'Pending'}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => setDeleteTarget(notification)}
                            className="p-1.5 hover:bg-background rounded-lg text-text-muted hover:text-danger transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={limit}
            onPageChange={setPage}
            itemName="notifications"
          />
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete notification?"
        description="This will remove the selected notification. This cannot be undone."
        confirmLabel="Delete"
        loading={deleteNotification.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.size} notification${selectedIds.size === 1 ? '' : 's'}?`}
        description="This will remove the selected notifications. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.size} notification${selectedIds.size === 1 ? '' : 's'}`}
        loading={bulkDeleteNotifications.isPending}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />

      <RetentionPurgeModal
        open={purgeOpen}
        loading={saveRetentionPolicy.isPending}
        itemSingular="notification"
        itemPlural="notifications"
        value={retentionValue}
        unit={retentionUnit}
        onValueChange={setRetentionValue}
        onUnitChange={setRetentionUnit}
        preview={purgePreview?.data}
        previewLoading={purgePreviewLoading}
        previewError={purgePreviewError}
        nextRunAt={retentionData?.data?.schedule?.nextRunAt}
        timezone={retentionData?.data?.schedule?.timezone}
        onClose={() => setPurgeOpen(false)}
        onConfirm={handleSavePolicy}
      />
    </div>
  );
}
